import React, { useState } from 'react';
type DialTone = 'gold' | 'light' | 'charcoal' | 'night';
type DialProps = {
  tone?: DialTone;
  numerals?: boolean | 'twelve';
  hands?: boolean;
  animated?: boolean;
  star?: boolean;
  wordmark?: boolean;
  className?: string;
  label?: string;
};
const ROMAN = ['XII', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI'];
const TONES: Record<DialTone, {
  line: string;
  index: string;
  hand: string;
  text: string;
  second: string;
}> = {
  gold: {
    line: '#B8976A',
    index: '#B8976A',
    hand: '#1E1E1E',
    text: '#1E1E1E',
    second: '#B8976A'
  },
  light: {
    line: 'rgba(255,255,255,0.75)',
    index: '#FFFFFF',
    hand: '#FFFFFF',
    text: '#FFFFFF',
    second: '#F2D4D0'
  },
  charcoal: {
    line: '#1E1E1E',
    index: '#B8976A',
    hand: '#1E1E1E',
    text: '#1E1E1E',
    second: '#B8976A'
  },
  night: {
    line: '#B8976A',
    index: '#B8976A',
    hand: '#FAF7F2',
    text: '#FAF7F2',
    second: '#B8976A'
  }
};
function polar(r: number, deg: number) {
  const rad = (deg - 90) * Math.PI / 180;
  return {
    x: 200 + r * Math.cos(rad),
    y: 200 + r * Math.sin(rad)
  };
}
function starPath(r: number) {
  const pts: string[] = [];
  let i = 0;
  for (let k = 0; k < 12; k++) {
    const p = polar(r, i * 30);
    pts.push(`${k === 0 ? 'M' : 'L'}${p.x.toFixed(2)} ${p.y.toFixed(2)}`);
    i = (i + 5) % 12;
  }
  return `${pts.join(' ')} Z`;
}
function useInitialAngles() {
  const [angles] = useState(() => {
    const d = new Date();
    const s = d.getSeconds() + d.getMilliseconds() / 1000;
    const m = d.getMinutes() + s / 60;
    const h = d.getHours() % 12 + m / 60;
    return {
      h: h * 30,
      m: m * 6,
      s: s * 6
    };
  });
  return angles;
}
export function Dial({
  tone = 'gold',
  numerals = true,
  hands = true,
  animated = true,
  star = true,
  wordmark = false,
  className = '',
  label
}: DialProps) {
  const c = TONES[tone];
  const base = useInitialAngles();
  const handClass = animated ? 'dial-hand' : '';
  return <svg viewBox="0 0 400 400" className={className} role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true} data-id="element-127" data-mp="3ipsi9y5cwy4w">
      <circle cx="200" cy="200" r="196" fill="none" stroke={c.line} strokeWidth="0.6" data-id="element-128" data-mp="kxis6iqf0jfu" />
      <circle cx="200" cy="200" r="186" fill="none" stroke={c.line} strokeWidth="0.4" strokeOpacity="0.7" data-id="element-129" data-mp="yodv7ee8g6bi" />
      <circle cx="200" cy="200" r="126" fill="none" stroke={c.line} strokeWidth="0.35" strokeOpacity="0.45" data-id="element-130" data-mp="up52mcuxiehj" />
      <circle cx="200" cy="200" r="64" fill="none" stroke={c.line} strokeWidth="0.35" strokeOpacity="0.35" data-id="element-131" data-mp="6ysrkt4e01mq" />
      {star && <path d={starPath(126)} fill="none" stroke={c.line} strokeWidth="0.35" strokeOpacity="0.35" data-id="element-132" data-mp="2192ey65i4g0" />}

      {Array.from({
      length: 60
    }).map((_, i) => {
      const major = i % 5 === 0;
      const a = polar(186, i * 6);
      const b = polar(major ? 168 : 180, i * 6);
      return <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={major ? c.index : c.line} strokeWidth={major ? i === 0 ? 2.2 : 1.3 : 0.5} strokeLinecap="round" data-id="element-133" data-mp="1rg774tn9e5mw" data-mpi={i} />;
    })}

      {numerals && ROMAN.map((n, i) => {
      if (numerals === 'twelve' && i !== 0) return null;
      const p = polar(150, i * 30);
      return <text key={n} x={p.x} y={p.y + 4.5} textAnchor="middle" fontFamily="'Barlow Condensed', sans-serif" fontSize="13" fontWeight="300" letterSpacing="1.5" fill={c.text} data-id="element-134" data-mp="1pxqfcpc2zr0z" data-mpi={i}>
              {n}
            </text>;
    })}

      {wordmark && <text x="203" y="168" textAnchor="middle" fontFamily="'Jost', sans-serif" fontSize="8.5" fontWeight="400" letterSpacing="3.4" fill={c.text} data-id="element-135" data-mp="28ui7zpdijihp">
          TRUE TWELVE
        </text>}

      {hands && <>
          <g transform={`rotate(${base.h} 200 200)`} data-id="element-136" data-mp="1crugxl7kgx6n">
            <g className={handClass} style={{
          animationDuration: '43200s'
        }} data-id="element-137" data-mp="18o6a54qre5mg">
              <line x1="200" y1="214" x2="200" y2="118" stroke={c.hand} strokeWidth="2.4" strokeLinecap="round" data-id="element-138" data-mp="13plbvav2hw0n" />
            </g>
          </g>
          <g transform={`rotate(${base.m} 200 200)`} data-id="element-139" data-mp="yrjrmmlwnqnl">
            <g className={handClass} style={{
          animationDuration: '3600s'
        }} data-id="element-140" data-mp="1weadrc3y65dg">
              <line x1="200" y1="220" x2="200" y2="66" stroke={c.hand} strokeWidth="1.3" strokeLinecap="round" data-id="element-141" data-mp="1wgd1tqljhofj" />
            </g>
          </g>
          <g transform={`rotate(${base.s} 200 200)`} data-id="element-142" data-mp="1mnxj2r2f4vtl">
            <g className={handClass} style={{
          animationDuration: '60s'
        }} data-id="element-143" data-mp="1mme3l2ol6osv">
              <line x1="200" y1="234" x2="200" y2="40" stroke={c.second} strokeWidth="0.7" strokeLinecap="round" data-id="element-144" data-mp="1hokzcf6ug4v8" />
            </g>
          </g>
          <circle cx="200" cy="200" r="3.4" fill={c.hand} data-id="element-145" data-mp="2ae1o92v993mp" />
          <circle cx="200" cy="200" r="1.3" fill={c.second} data-id="element-146" data-mp="2acgtpd8xxkou" />
        </>}
    </svg>;
}